// @ts-nocheck
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getOrderStatusMappingDataView } from "@insite/client-framework/Store/Data/OrderStatusMappings/OrderStatusMappingsSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import { OrderStatusMappingModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SearchFieldWrapper, {
    SearchFieldWrapperStyles,
} from "@insite/content-library/Widgets/OrderHistory/SearchFieldWrapper";
import Select, { SelectProps } from "@insite/mobius/Select";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

/* Store Custom Code - Start */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable from "@insite/mobius/Clickable";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import InfoFill from "@store/Library/Components/Toolkit/Icons/Info/InfoFill";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import parse from "html-react-parser";
import { css } from "styled-components";

const enum fields {
    orderStatusDetails = "orderStatusDetails",
}

type State = {
    modalIsOpen: boolean;
};
/* Store Custom Code - End */

interface OwnProps extends WidgetProps {
    /* Store Custom Code - Start */
    fields: {
        [fields.orderStatusDetails]: string;
    };
    /* Store Custom Code - End */
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        orderStatusMappings: getOrderStatusMappingDataView(state).value,
        parameter: state.pages.orderHistory.getOrdersParameter,
    };
};

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderHistorySearchFieldStatusStyles {
    select?: SelectProps;
    wrapper?: SearchFieldWrapperStyles;
    // Store Custom Code
    statusLabelWrapper?: InjectableCss;
    actionIcon?: IconProps;
    statusDetailsModal?: ModalPresentationProps;
}

export const statusStyles: OrderHistorySearchFieldStatusStyles = {
    /* Store Custom Code - Start */
    statusLabelWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(8)};
        `,
    },
    actionIcon: {
        src: InfoFill,
        size: 20,
    },
    /* Store Custom Code - End */
};
const styles = statusStyles;

class OrderHistorySearchFieldStatus extends React.Component<Props, State /* Store Custom Code */> {
    /* Store Custom Code - Start */
    constructor(props: Props) {
        super(props);
        this.state = {
            modalIsOpen: false,
        };
    }

    toggleStatusDetailsModal = () => {
        this.setState({ modalIsOpen: !this.state.modalIsOpen });
    };

    orderStatusDescTitle = translate("Order Status Descriptions");
    /* Store Custom Code - End */

    handleChange = (event: React.FormEvent<HTMLSelectElement>) => {
        if (event.currentTarget.value) {
            this.props.updateSearchFields({
                status: event.currentTarget.value.split(","),
                displayName: findDisplayName(event.currentTarget.value, this.props.orderStatusMappings),
            });
        } else {
            this.props.updateSearchFields({ status: undefined });
        }
    };

    render() {
        const options = this.props.orderStatusMappings || [];
        const uniqueOptions = new Map<string, string[]>();
        for (const option of options) {
            const erpOrderStatuses = uniqueOptions.get(option.displayName) || [];
            erpOrderStatuses.push(option.erpOrderStatus);
            uniqueOptions.set(option.displayName, erpOrderStatuses);
        }

        const value =
            this.props.parameter.status && this.props.parameter.status.length > 0
                ? /* Store Custom Code - Start */
                  typeof this.props.parameter.status === "object"
                    ? this.props.parameter.status.join(",")
                    : this.props.parameter.status
                : /* Store Custom Code - End */
                  "";

        return (
            <SearchFieldWrapper extendedStyles={styles.wrapper}>
                <Select
                    /* Store Custom Code - Start */
                    label={
                        <StyledWrapper {...styles.statusLabelWrapper}>
                            {translate("Order Status")}
                            <Clickable onClick={this.toggleStatusDetailsModal}>
                                <IconMemo {...styles.actionIcon} title={this.orderStatusDescTitle} />
                            </Clickable>
                        </StyledWrapper>
                    }
                    /* Store Custom Code - End */
                    {...styles.select}
                    value={value}
                    onChange={this.handleChange}
                    data-test-selector="orderHistory_filterStatus"
                >
                    <option value="">{translate("Select")}</option>
                    {Array.from(uniqueOptions, ([name, values]) => ({ name, values })).map(option => (
                        <option key={option.values.join(",")} value={option.values.join(",")}>
                            {option.name}
                        </option>
                    ))}
                </Select>
                {/* Store Custom Code - Start */}
                <Modal
                    headline={this.orderStatusDescTitle}
                    isOpen={this.state.modalIsOpen}
                    handleClose={this.toggleStatusDetailsModal}
                    {...styles.statusDetailsModal}
                >
                    {parse(this.props.fields.orderStatusDetails, parserOptions)}
                </Modal>
                {/* Store Custom Code - End */}
            </SearchFieldWrapper>
        );
    }
}

/* Store Custom Code - Start */
export function findDisplayName(erpOrderStatus: string, orderStatusMappings: OrderStatusMappingModel[] = []) {
    const arrOfStatus = erpOrderStatus.split(",");

    let returnVal;
    for (let i = 0; i < orderStatusMappings.length; i++) {
        const option = orderStatusMappings[i];
        if (arrOfStatus.includes(option.erpOrderStatus)) {
            returnVal = option.displayName;
            break;
        }
    }
    return returnVal;
}
/* Store Custom Code - End */

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderHistorySearchFieldStatus),
    definition: {
        group: "Order History",
        displayName: "Status",
        allowedContexts: ["OrderHistoryPage", "VmiOrderHistoryPage"],
        /* Store Custom Code - Start */
        fieldDefinitions: [
            {
                name: fields.orderStatusDetails,
                fieldType: "Translatable",
                editorTemplate: "RichTextField",
                defaultValue:
                    "<p><strong>Pending Confirmation</strong><br>The order has been registered, but order details must be confirmed by Greif&rsquo;s Customer Service Representative.</p><p><strong>Confirmed</strong><br>The order details have been confirmed by Greif&rsquo;s Customer Service Representative.</p><p><strong>Invoicing in Progress</strong><br>The order has been shipped from Greif&rsquo;s Plant and the order has at least one related Greif sales invoice document.</p><p><strong>Shipping in Progress</strong><br>The order has been shipped from Greif&rsquo;s Plant at least one related shipment document.</p><p><strong>Fulfilled</strong><br>The order has been shipped from Greif&rsquo;s Plant and Greif sales invoicing is complete.</p><p><strong>Cancelled</strong><br>The order has been cancelled and Greif will not fulfil the order.</p>",
            },
        ],
        /* Store Custom Code - End */
    },
};

export default widgetModule;
